import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
  CardActions,
  Divider,
  Button,
  Box,
  Grid,
  Typography,
  TextField,
  Tooltip
} from '@material-ui/core';
import TemplateEditor from 'components/Template/TemplateEditor';
import TemplateKeyBank from 'components/Template/TemplateKeyBank';
import formSurveyKeys from 'components/Template/TemplateEditor/formSurveyKeys';
import VerifyTemplate from 'views/UserMessaging/customModalContent/verifyTemplate';
import { constants } from 'views/Campaign';
import SaveMessage from 'views/UserMessaging/customModalContent/saveMessage';
import SaveConfirm from 'views/UserMessaging/customModalContent/saveConfirmTemplate';
import { Alert } from '@material-ui/lab';
import { CloseIcon } from 'components';
import { getEnvVar } from 'helpers';
import { ProductType } from 'constants.js';
import { isReadOnlyMode } from 'store/helpers/helperFunctions';
import { useLanguageSettings } from '../../../Settings/useLanguageSettings';

const useStyles = makeStyles(theme => ({
  root: {},
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%'
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    width: '95%'
  },
  autoSave: {
    marginLeft: '40%'
  },
  cardHeader: {},
  cardActions: {
    flexDirection: 'row'
  },
  inputGrid: {
    maxWidth: '40rem'
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20%'
  }
}));

const Design = props => {
  const {
    className,
    staticContext,
    template,
    back,
    showNav,
    isPopupDialog,
    isIFrame,
    ...rest
  } = props;
  const {
    allSurveys,
    isLoading,
    shouldAutoSave,
    isAutoSaveSuccess,
    isTestingTemplate,
    isTestingSucceeded,
    isTemplateSaved,
    isTemplateSaving,
    goBack,
    navBackOnError,
    thisTemplate,
    handleChange,
    saveTemplate,
    onFocus,
    onBlur,
    closeTemplate,
    openTestTemplateModal,
    closeTestTemplateModal,
    shouldShowError,
    getErrorMessage,
    getTestTemplate,
    updateCurrentErrorValidationSegment,
    validateTestTemplate,
    modifyPageIterator,
    updateCurrentTemplate,
    startAutoSaveTimer,
    autoSaveTemplate,
    isTemplateReadOnly,
    showWarningMessage,
    isTenantDefaultTemplate
  } = template;

  const {
    campaignOccurrenceTypes: { INTERNAL_SURVEY_RESPONSE, EXTERNAL_SURVEY_RESPONSE }
  } = constants;

  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [isValidationError, setIsValidationError] = useState({});

  const classes = useStyles();
  const editorRef = React.createRef();
  const dateTimeNowTemplateKey = (label, key) => {
    return {
      dataType: 'String',
      key: key,
      name: label,
      text: `{{{${key}}}}`
    };
  };

  const { templateLanguageContent, getTemplateLanguageContent } = useLanguageSettings();
  const [detailsContent, setDetailsContent] = useState(null);

  const templateKeys = () => {
    let tempKeys = thisTemplate.eventMetaData;
    tempKeys = tempKeys.map(keys => {
      let schemaConditionAttributes =
        keys.schemaConditionAttributes &&
        keys.schemaConditionAttributes.filter(
          i => i.attributeFieldType !== 'dataFrame'
        );
      if ((keys.fieldFromSchema || keys.schemaType === "EVENT") || (thisTemplate.productType === ProductType.GUEST_INSIGHTS && keys.schemaType === "GUEST_INSIGHT")) {
        schemaConditionAttributes = [
          ...schemaConditionAttributes,
          dateTimeNowTemplateKey(
            detailsContent?.dateTimeNowLabel || `Date Time Now`,
            detailsContent?.dateTimeNowKey || `dateTimeNow`
          )
        ];
        if ((thisTemplate.campaignOccurrenceType !== INTERNAL_SURVEY_RESPONSE &&
            thisTemplate.campaignOccurrenceType !== EXTERNAL_SURVEY_RESPONSE)) {
          if (allSurveys?.length > 0) {
            let tempSurveys = formSurveyKeys(allSurveys, thisTemplate, detailsContent?.surveysLabel || `Surveys`);
            if (tempSurveys?.surveyMenuOptions?.length > 0) {
              schemaConditionAttributes = [
                ...schemaConditionAttributes,
                tempSurveys
              ];
            }
          }
        }
      }
      return { ...keys, schemaConditionAttributes: schemaConditionAttributes };
    });
    return tempKeys;
  };
  useEffect(() => {
    if (!isLoading) {
      startAutoSaveTimer();
    }
  }, [isLoading]);

  useEffect(() => {
    updateCurrentErrorValidationSegment('design');
    if(!isIFrame) {
      navBackOnError(back);
    }
  }, [thisTemplate]);

  useEffect(() => {
    if (isTemplateSaved) {
      startAutoSaveTimer();
    }
  }, [isTemplateSaved]);

  useEffect(() => {
    if (shouldAutoSave) {
      thisTemplate.type === 'EMAIL'
        ? editorRef.current.exportAndSaveTemplate({
            type: 'autoSaveTemplate'
          })
        : autoSaveTemplate();
    }
  }, [shouldAutoSave]);

  useEffect(() => {
    if (templateLanguageContent) {
      setDetailsContent(getTemplateLanguageContent("communicationTemplateDesign"));
    }
  }, [templateLanguageContent]);

  const onClickSaveButton = () => {
    if (showWarningMessage()) {
      setShowSaveConfirmModal(true);
    } else {
      thisTemplate.type === 'EMAIL'
        ? editorRef.current.exportAndSaveTemplate()
        : saveTemplate();
    }
  };

  const saveConfirmTemplate = () => {
    setShowSaveConfirmModal(false);
    thisTemplate.type === 'EMAIL'
      ? editorRef.current.exportAndSaveTemplate()
      : saveTemplate();
  };

  const closeSaveConfirm = () => {
    setShowSaveConfirmModal(false);
  };

  const validateErrors = () => {
    let err = Object.values(isValidationError).filter(val => val.isDisabled);
    return err.length > 0 ? true : false;
  };

  return (
    <div className={classes.root}>
      {thisTemplate?.eventMetaData ? (
        <Card {...rest} className={clsx(classes.root, className)}>
          <form autoComplete="off">
            {!isPopupDialog && (
              <React.Fragment>
                <div>
                  {isReadOnlyMode() ? (
                    <Alert severity="info">
                      {detailsContent?.readOnlyModeAlert || `You can only view this and do not have access to edit.`}
                    </Alert>
                  ): (
                    isTemplateReadOnly() && (
                      <Alert severity="info">
                        {detailsContent?.templateReadOnlyModeAlert || `You can only view this template and do not have access to edit.`}{' '}
                        {isTenantDefaultTemplate()
                          ? `${detailsContent?.tenantDefaultAlert || `Tenant Default template can be edited only with 'ALL' property.`}`
                          : `${detailsContent?.globalDefaultAlert || `Only admin can edit Global Default template.`}`}
                      </Alert>)
                  )}
                </div>
                <div className={classes.headerContainer}>
                  <div className={classes.title}>
                    <CardHeader
                      subheader={detailsContent?.pageSubHeader || `* indicates a required field`}
                      title={detailsContent?.pageTitle || `Template Design`}
                      titleTypographyProps={{ variant: 'h1' }}
                    />
                  </div>
                  {!isIFrame && (
                    <CloseIcon onClickClose={closeTemplate} />
                  )}
                </div>
              </React.Fragment>
            )}
            <CardContent>
              {thisTemplate.type === 'EMAIL' && (
                <TemplateEditor
                  ref={editorRef}
                  thisTemplate={thisTemplate}
                  templateKeys={thisTemplate.eventMetaData}
                  surveys={(
                    thisTemplate.campaignOccurrenceType !==
                    INTERNAL_SURVEY_RESPONSE &&
                    thisTemplate.campaignOccurrenceType !==
                    EXTERNAL_SURVEY_RESPONSE)
                      ? allSurveys
                      : []
                  }
                  useUnsubscribeLinkOption={(
                    thisTemplate.campaignOccurrenceType !==
                    INTERNAL_SURVEY_RESPONSE)
                    ? true
                    : false
                  }
                  useSurveysLinkOption={true}
                  handleChange={handleChange}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  saveTemplate={saveTemplate}
                  validateTestTemplate={validateTestTemplate}
                  shouldAutoSave={shouldAutoSave}
                  autoSaveTemplate={autoSaveTemplate}
                  isTemplateReadOnly={isTemplateReadOnly}
                  modifyPageIterator={modifyPageIterator}
                  setIsValidationError={setIsValidationError}>
                  <TextField
                    fullWidth
                    disabled={isTemplateReadOnly()}
                    label={detailsContent?.emailSubjectLabel || `Email Subject Line`}
                    margin="dense"
                    name="emailSubject"
                    onChange={handleChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    placeholder={detailsContent?.emailSubjectPlaceholder || `Hello {{firstName}}`}
                    autoComplete="emailSubject"
                    required
                    value={thisTemplate.emailSubject || ''}
                    variant="outlined"
                    error={shouldShowError('emailSubject')}
                    helperText={getErrorMessage('emailSubject')}
                  />
                  {shouldAutoSave && (
                    <Typography
                      color="textSecondary"
                      variant="body2"
                      className={classes.autoSave}>
                      {detailsContent?.autoSavingLabel || `AutoSaving...`}
                    </Typography>
                  )}
                </TemplateEditor>
              )}
              {thisTemplate.type === 'SMS' && (
                <Grid container spacing={3}>
                  <Grid item md={6} xs={12}>
                    {shouldAutoSave && (
                      <Typography
                        color="textSecondary"
                        variant="body2"
                        className={classes.autoSave}>
                        {detailsContent?.autoSavingLabel || `AutoSaving...`}
                      </Typography>
                    )}
                    <Grid item xs={12} className={classes.inputGrid}>
                      <TextField
                        fullWidth
                        disabled={isTemplateReadOnly()}
                        label={detailsContent?.smsContentLabel || `Content`}
                        margin="dense"
                        name="content"
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onChange={handleChange}
                        required
                        multiline
                        rows="4"
                        value={thisTemplate.content || ''}
                        variant="outlined"
                        error={shouldShowError('content')}
                        helperText={getErrorMessage('content')}
                      />
                    </Grid>
                  </Grid>
                  {!isTemplateReadOnly() && (
                    <Grid item md={6} xs={12}>
                      <Typography variant="h3">{detailsContent?.reservationKeysLabel || `Reservation Keys`}</Typography>
                      <Typography variant="body2">
                        {detailsContent?.reservationKeysSubHeader || `Select a key to copy it to the clip board and then paste it into your template.`}
                      </Typography>
                      <TemplateKeyBank templateKeys={templateKeys()} isOpen={true} type={"SMS"}/>
                    </Grid>
                  )}
                </Grid>
              )}
            </CardContent>
            {(back || showNav || isPopupDialog) && (
              <>
                {!isPopupDialog && <Divider />}
                <CardActions className={classes.cardActions}>
                  {!isPopupDialog && !isIFrame && (
                    <Button
                      color="secondary"
                      variant="contained"
                      disabled={isLoading}
                      link={back}
                      onClick={goBack}>
                      {detailsContent?.backButtonLabel || `Back`}
                    </Button>
                  )}
                  <Typography></Typography>
                  <Box flexGrow={1}>&nbsp;</Box>
                  {(thisTemplate.productType !== ProductType.List ||
                    getEnvVar('ADM_INCLUDE_LIST_MANAGEMENT_PHASE2') ===
                      'true') && (
                    <React.Fragment>
                      <VerifyTemplate
                        templateType={thisTemplate.type}
                        openModel={openTestTemplateModal}
                        closeModel={closeTestTemplateModal}
                        getTestTemplate={getTestTemplate}
                        isTestingTemplate={isTestingTemplate}
                        isTestingSucceeded={isTestingSucceeded}
                      />
                      <Button
                        color="primary"
                        variant="contained"
                        disabled={isLoading || isTemplateSaving}
                        onClick={() =>
                          thisTemplate.type === 'EMAIL'
                            ? editorRef.current.exportAndSaveTemplate({
                                type: 'testTemplate'
                              })
                            : validateTestTemplate()
                        }>
                        {detailsContent?.testTemplateButtonLabel|| `Test Template`}
                      </Button>
                    </React.Fragment>
                  )}
                  {!isTemplateReadOnly() &&
                    (validateErrors() ? (
                      <Tooltip title={detailsContent?.saveTemplateErroHelper || `Fix the handlebar formats`}>
                        <span>
                          <Button
                            color="primary"
                            variant="contained"
                            disabled={() => validateErrors}
                            onClick={() => onClickSaveButton()}>
                            {detailsContent?.saveTemplateButtonLabel || `Save Template`}
                          </Button>
                        </span>
                      </Tooltip>
                    ) : (
                      <Button
                        color="primary"
                        variant="contained"
                        disabled={isLoading || isTemplateSaving}
                        onClick={() => onClickSaveButton()}>
                        {detailsContent?.saveTemplateButtonLabel || `Save Template`}
                      </Button>
                    ))}
                </CardActions>
              </>
            )}
          </form>
        </Card>
      ) : (
        <div className={classes.loader}>
          <CircularProgress />
        </div>
      )}
      {!isAutoSaveSuccess && <SaveMessage openSnackbar={isTemplateSaved} />}
      {showSaveConfirmModal && (
        <SaveConfirm
          closeModel={closeSaveConfirm}
          saveConfirmTemplate={saveConfirmTemplate}></SaveConfirm>
      )}
    </div>
  );
};

export default Design;
